import React from 'react';
import { createPortal } from 'react-dom';

interface BottomSheetModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  showConfirmButton?: boolean;
  onConfirm?: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmDisabled?: boolean;
  useGrayBackground?: boolean; // 新增：是否使用灰色背景（用于在EditModal中显示）
  zIndex?: number; // 新增：自定义 z-index 层级
  usePortal?: boolean; // 新增：是否使用 Portal 渲染到 body
}

/**
 * 通用的iOS风格底部弹窗组件
 * 抽取自 ChannelSelector，用于所有从底部滑上来的弹窗
 */
export const BottomSheetModal: React.FC<BottomSheetModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  showConfirmButton = true,
  onConfirm,
  confirmText = '保存',
  cancelText = '取消',
  confirmDisabled = false,
  useGrayBackground = false,
  zIndex = 999,
  usePortal = false,
}) => {
  // 处理确认
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    } else {
      onClose();
    }
  };

  // 处理取消
  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      {/* 背景遮罩 */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        style={{ zIndex }}
        onClick={handleCancel}
      />

      {/* 弹窗面板 */}
      <div
        className={`fixed bottom-0 left-0 right-0 max-w-md mx-auto rounded-t-ios-xl shadow-ios-modal dark:shadow-ios-dark-modal ${
          useGrayBackground
            ? 'bg-ios-gray6 dark:bg-ios-dark-gray6'
            : 'bg-white dark:bg-ios-dark-gray6'
        }`}
        style={{ zIndex: zIndex + 1 }}
      >
        {/* 标题栏 */}
        <div className="flex items-center justify-between p-4 border-b border-ios-gray5 dark:border-ios-dark-gray5">
          <button
            onClick={handleCancel}
            className="text-ios-blue dark:text-ios-dark-blue text-ios-body font-medium"
          >
            {cancelText}
          </button>
          <h3 className="text-ios-headline font-semibold">{title}</h3>
          {showConfirmButton ? (
            <button
              onClick={handleConfirm}
              disabled={confirmDisabled}
              className={`text-ios-body font-medium ${
                confirmDisabled
                  ? 'text-ios-gray dark:text-ios-dark-gray'
                  : 'text-ios-blue dark:text-ios-dark-blue'
              }`}
            >
              {confirmText}
            </button>
          ) : (
            <div className="w-12" /> // 占位保持居中
          )}
        </div>

        {/* 内容区域 */}
        <div className="pb-8 max-h-[calc(100vh-12rem)] overflow-y-auto">
          {children}
        </div>

        {/* 安全区域适配 */}
        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    </>
  );

  // 如果使用 Portal，渲染到 body；否则正常渲染
  return usePortal ? createPortal(modalContent, document.body) : modalContent;
};

export default BottomSheetModal;
